import { useEffect, useState, useRef } from "react";
import useErrorBoundary from "use-error-boundary";
import InfiniteScroll from "react-infinite-scroll-component";
import { useMediaQuery } from "react-responsive";
import throttle from "lodash.throttle";

import styles from "./style.module.css";
import { Policy } from "@src/API";
import { TypeofWork } from "types/enums";
import ResultsNotFound from "@components/ResultsNotFound";
import { useAuth } from "@src/auth";
import CardDetail from "./CardDetail";
import ApplicationCard from "./ApplicationCard";
import MobileJobDescriptionModal from "@components/MobileJobDescriptionModal";
import { useJobSearch } from "@src/state/jobSearch";
import { Button } from "semantic-ui-react";
import Link from "next/link";
import Interviews from "./Interviews";
// import FeedbackWidget from "./FeedbackWidget";
import { useAppState } from "@src/state";

type JobPosting = {
  image?: string;
  title: string;
  company: string;
  description: string;
  digitalNomadFriendly: boolean;
  remotePolicy: Policy;
  workType: TypeofWork;
  location?: string;
  salary?: {
    currencyCode: string;
    value: string;
  };
};

type JobsViewerProps = {
  jobSeekerId: string;
  jobPostings: JobPosting[] | [];
  hasMore?: boolean;
  status?: string;
  doNotClear?: boolean;
  dashboardPage?: boolean;
  onScroll: () => void;
  TopComponent?: React.ReactNode;
  applied?: boolean;
};

export default function JobsViewer({
  jobSeekerId,
  jobPostings = [],
  status,
  doNotClear,
  hasMore,
  dashboardPage,
  onScroll,
  TopComponent,
  applied
}: JobsViewerProps) {
  const { dispatch, state } = useJobSearch();
  const { state: appState, dispatch: appDispatch } = useAppState();
  const [activeIdx, setActiveIdx] = useState(-1);
  const [mobileScroll, setMobileScroll] = useState(0);

  const { isCandidate } = useAuth();
  const cardDetailRef = useRef();
  const scrollPos = useRef();

  const userIsCandidate = isCandidate();

  const { ErrorBoundary, didCatch, error } = useErrorBoundary();

  const apps = [];

  const isTabletOrMobile = useMediaQuery({ query: "(max-width: 1024px)" });

  const target = document.getElementById("scrollableDiv");
  const parent = document.getElementById("mobileViewer");

  function handleMobileScroll() {
    const scrollTop = scrollPos?.current?.scrollTop ?? 0;
    updateMobileScroll(scrollTop);
  }

  const updateMobileScroll = (val) => {
    const scrollTop = scrollPos?.current?.scrollTop ?? 0;

    console.log("scrollTop: ", scrollTop);
    console.log("val: ", val);

    if (Math.abs(val - mobileScroll) > 100) {
      if (val > mobileScroll) {
        if (!state.mobileScrollDown) {
          dispatch({ type: "setMobileScroll", payload: true });
        }
      } else {
        if (state.mobileScrollDown) {
          dispatch({ type: "setMobileScroll", payload: false });
        }
      }
      setMobileScroll(val);
    }
  };

  useEffect(() => {
    if (!doNotClear) {
      setActiveIdx(-1);
    }
  }, [jobPostings]);

  useEffect(() => {
    if (!isTabletOrMobile && jobPostings?.length && activeIdx === -1) {
      setActiveIdx(0);
    }
  }, [jobPostings]);

  useEffect(() => {
    if (isTabletOrMobile) {
      if (activeIdx >= 0) {
        hideSidebar()
      } else {
        showSidebar()
      }
    }
  }, [activeIdx])

  const showSidebar = () => {
    appDispatch({ type: "show_sidebar" });
  };

  const hideSidebar = () => {
    appDispatch({ type: "hide_sidebar" });
  };

  const handleClick = (idx) => () => {
    if (activeIdx === idx) {
      setActiveIdx(-1);
    } else {
      if (cardDetailRef && cardDetailRef?.current && cardDetailRef?.current) {
        cardDetailRef.current.scrollTop = 0;
      }
      setActiveIdx(idx);
    }
  };

  const handleMobileClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setActiveIdx(-1);
  };

  const renderMobileModal = () => {
    if (activeIdx > -1) {
      return (
        <MobileJobDescriptionModal
          selectedJob={jobPostings[activeIdx]}
          closeModal={() => setActiveIdx(-1)}
          dashboardPage={dashboardPage}
        />
      );
    } 
  };

  function renderFeed() {
    if (!isTabletOrMobile || dashboardPage) {
      return renderCards();
    }

    const cards = renderCards() ?? [];

    const AutoApply = () => (
      <div className={styles.autoApplyCard}>
        <h4>Auto-Apply</h4>
        <p>
          Automatically apply to the jobs you want as soon as they become
          available
        </p>
        {userIsCandidate ? (
          <Link href="/candidate/dashboard/auto/" passHref>
            <a>
              <Button color="blue" circular>
                Get Started
              </Button>
            </a>
          </Link>
        ) : (
          <Link href="/auto/" passHref>
            <a>
              <Button color="blue" circular>
                Get Started
              </Button>
            </a>
          </Link>
        )}
      </div>
    );

    if (cards.length < 10) {
      cards.push(<AutoApply />);
    } else {
      cards.splice(9, 0, <AutoApply />);
    }
    return cards;
  }

  const renderCards = () => {
    const cards = jobPostings.map((post, idx) => {
      let applicationDate = post?.dateApplied;

      if (idx === 0) {
        return (
          <ErrorBoundary key={post.id}>
            <ApplicationCard
              key={post.id}
              status={status}
              onClick={handleClick(idx)}
              active={activeIdx === idx}
              post={post}
              applicationDate={applicationDate}
              dashboardCard={dashboardPage}
              shouldRenderApplied={applied}
            />
          </ErrorBoundary>
        );
      }
      return (
        <ErrorBoundary key={post.id}>
          <ApplicationCard
            key={post.id}
            status={status}
            onClick={handleClick(idx)}
            active={activeIdx === idx}
            post={post}
            applicationDate={applicationDate}
            dashboardCard={dashboardPage}
            shouldRenderApplied={applied}
          />
        </ErrorBoundary>
      );
    });

    return cards;
  };

  if (jobPostings.length === 0) {
    let subheaderStyle;
    let headerText = "No results found";
    let containerStyle;
    let headerStyle;
    let wrapperStyle = {};
    let buttonStyle;
    let subheaderText =
      "Create an alert. We'll email you when we have what you're looking for.";

    if (isTabletOrMobile) {
      subheaderStyle = {
        fontSize: "19px",
        width: "315px",
        maxWidth: "100%",
        lineHeight: "25px",
        marginTop: "17px",
      };
      containerStyle = { marginTop: "10px" };
      headerStyle = { lineHeight: "20px" };
    } else {
      headerStyle = {
        marginTop: "30px",
      };

      subheaderStyle = {
        fontSize: "20px",
        width: "350px",
        marginTop: "13px",
      };

      wrapperStyle = {
        paddingTop: "49px",
      };

      buttonStyle = {
        marginTop: "27px",
      };
    }

    wrapperStyle.border = "none";
    wrapperStyle.boxShadow = "none";

    const openAlert = () =>
      dispatch({ type: "setAlertVisibility", payload: true });

    return (
      <div className={styles.container}>
        <ResultsNotFound
          subheaderStyle={subheaderStyle}
          headerText={headerText}
          containerStyle={containerStyle}
          headerStyle={headerStyle}
          wrapperStyle={wrapperStyle}
          buttonStyle={buttonStyle}
          subheaderText={subheaderText}
          buttonText={"Create job alert"}
          buttonHandler={openAlert}
        />
      </div>
    );
  }

  if (isTabletOrMobile) {
    return (
      <div className={styles.container} id="mobileViewer">
        {renderMobileModal()}
        <div
          className={styles.mobileCardColumn}
          id="scrollableDiv"
          onScroll={throttle(handleMobileScroll, 1000)}
          ref={scrollPos}
        >
          <div className={`${styles.cardWrapper} ${dashboardPage ? styles.cardWrapperDashboard : ''}`} ref={scrollPos}>
            <InfiniteScroll
              dataLength={jobPostings.length}
              next={onScroll}
              hasMore={hasMore}
              loader={<ApplicationCard key={"empty card"} empty />}
              scrollThreshold={0.7}
              scrollableTarget="scrollableDiv"
            >
              <ErrorBoundary>{renderFeed()}</ErrorBoundary>
            </InfiniteScroll>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div
        id="test"
        className={`${styles.cardColumn} ${
          activeIdx === -1 ? styles.fullWidth : ""
        }`}
      >
        {TopComponent && <TopComponent />}
        <div className={styles.cardWrapper}>
          <InfiniteScroll
            dataLength={jobPostings.length}
            next={onScroll}
            hasMore={hasMore}
            loader={<ApplicationCard key={"empty card"} empty />}
            scrollThreshold={0.4}
            scrollableTarget="test"
          >
            <ErrorBoundary>{renderCards()}</ErrorBoundary>
          </InfiniteScroll>
        </div>
      </div>
      <CardDetail
        applications={apps}
        status={status}
        jobSeekerId={jobSeekerId}
        idx={activeIdx}
        postings={jobPostings}
        innerRef={cardDetailRef}
      />
    </div>
  );
}
